// @moreyears/icons - Web Component
// 
// Usage via CDN:
//   <script src="https://cdn.jsdelivr.net/npm/@moreyears/icons/dist/web-component.js"></script>
//   <moreyears-icon name="mailbox" weight="bold" size="24" color="#333"></moreyears-icon>
//
// Usage via npm:
//   import '@moreyears/icons/web-component';
//   // Then use <moreyears-icon> anywhere in your HTML

import manifest from './manifest.json';
import { svgRegistry } from './svg-registry';

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/,\s*/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const WEIGHT_SLUG_MAP = {
  'broken': 'broken',
  'line-duotone': 'line-duotone',
  'linear': 'linear',
  'outline': 'outline',
  'bold': 'bold',
  'bold-duotone': 'bold-duotone'
};

function resolveIcon(name) {
  if (manifest.icons[name]) return manifest.icons[name];
  const slugged = slugify(name);
  if (manifest.icons[slugged]) return manifest.icons[slugged];
  for (const icon of Object.values(manifest.icons)) {
    if (icon.slug === slugged || icon.slug === name) return icon;
  }
  const lowerName = name.toLowerCase();
  for (const icon of Object.values(manifest.icons)) {
    if (icon.name.toLowerCase() === lowerName) return icon;
  }
  return null;
}

class MoreYearsIcon extends HTMLElement {
  static get observedAttributes() {
    return ['name', 'weight', 'size', 'color', 'stroke-width'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    this.render();
  }

  render() {
    const name = this.getAttribute('name');
    const weight = this.getAttribute('weight') || 'linear';
    const size = this.getAttribute('size') || '24';
    const color = this.getAttribute('color') || 'currentColor';
    const strokeWidth = this.getAttribute('stroke-width');

    if (!name) {
      this.shadowRoot.innerHTML = '';
      return;
    }

    const icon = resolveIcon(name);
    if (!icon) {
      console.warn(`[@moreyears/icons] Icon not found: "${name}"`);
      this.shadowRoot.innerHTML = '';
      return;
    }

    const weightSlug = WEIGHT_SLUG_MAP[weight] || slugify(weight);
    const weightEntry = icon.weights.find(w => w.weightSlug === weightSlug);
    if (!weightEntry) {
      console.warn(`[@moreyears/icons] Weight "${weight}" not available for "${name}". Available: ${icon.weights.map(w => w.weight).join(', ')}`);
      this.shadowRoot.innerHTML = '';
      return;
    }

    let svg = svgRegistry[weightEntry.path];
    if (!svg) {
      this.shadowRoot.innerHTML = '';
      return;
    }

    // Inject size
    svg = svg.replace('<svg', `<svg width="${size}" height="${size}"`);

    // Set color
    svg = svg.replace(/fill="(?!none)[^"]*"/g, `fill="${color}"`);
    svg = svg.replace(/stroke="(?!none)[^"]*"/g, `stroke="${color}"`);

    // Apply stroke width if specified
    if (strokeWidth) {
      svg = svg.replace(/stroke-width="[^"]*"/g, `stroke-width="${strokeWidth}"`);
    }

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: ${size}px;
          height: ${size}px;
          line-height: 0;
        }
        svg {
          width: 100%;
          height: 100%;
        }
      </style>
      ${svg}
    `;
  }
}

// Register the custom element
if (!customElements.get('moreyears-icon')) {
  customElements.define('moreyears-icon', MoreYearsIcon);
}

export { MoreYearsIcon };
